import { useCallback, useEffect, useState } from 'react';
import { Collapse } from 'antd';
import callApi from '../../Api/config';
import { DOCUMITRA_MEMBER_STAGE_HISTORY } from '../../constants/EntityName';
import htmlToPlainText from '../../service/htmlToPlainText';
import GenericTable from '../GenericTable';
import { InvertFillButton } from '../InvertFillButton';

const { Panel } = Collapse;
const REMINDER_HISTORY_ACTION = 'MISSING_DOCUMENTS_EMAIL_SENT';

interface ReminderHistoryRow {
  id: string;
  sentOn: string;
  documents: string;
  remark: string;
  sentBy: string;
}

interface DocumentReminderHistoryProps {
  member: any;
  refreshKey?: number;
}

const formatSentOn = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toHistoryRow = (entry: any): ReminderHistoryRow => {
  const documents: any[] = Array.isArray(entry?.metadata?.documents)
    ? entry.metadata.documents
    : [];
  const perDocumentRemarks = documents
    .map(doc => {
      const plainRemark = htmlToPlainText(doc?.remarks);
      return plainRemark ? `${doc.name}: ${plainRemark}` : '';
    })
    .filter(Boolean)
    .join(' | ');
  const additionalInstructions = htmlToPlainText(entry?.metadata?.additionalInstructions);

  return {
    id: String(entry?.id),
    sentOn: formatSentOn(entry?.createdAt),
    documents:
      documents
        .map(doc => doc?.name)
        .filter(Boolean)
        .join(', ') || '—',
    remark: additionalInstructions || perDocumentRemarks || '—',
    sentBy: entry?.actorName || 'Admin',
  };
};

const DocumentReminderHistory = ({ member, refreshKey = 0 }: DocumentReminderHistoryProps) => {
  const [history, setHistory] = useState<ReminderHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  const fetchHistory = useCallback(async () => {
    if (!member?.id) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const res = await callApi(`${DOCUMITRA_MEMBER_STAGE_HISTORY}/${member.id}`, 'GET');
      const entries: any[] = Array.isArray(res?.data?.data) ? res.data.data : [];
      setHistory(
        entries
          .filter(entry => entry?.action === REMINDER_HISTORY_ACTION)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .map(toHistoryRow)
      );
    } catch (error) {
      console.error('Error loading reminder history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [member?.id]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshKey]);

  return (
    <Collapse
      className="document-reminder-history-collapse"
      style={{ marginTop: '16px' }}
      defaultActiveKey={['reminder-history']}
    >
      <Panel
        header={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Documents Request History</span>
            <InvertFillButton
              variant="white"
              size="sm"
              fontSize="sm"
              fontWeight="semibold"
              onClick={event => {
                event.stopPropagation();
                fetchHistory();
              }}
              disabled={historyLoading}
            >
              Refresh
            </InvertFillButton>
          </div>
        }
        key="reminder-history"
      >
        <GenericTable
          style={{ padding: '20px' }}
          rowKey="id"
          pagination={false}
          bordered
          loading={historyLoading}
          dataSource={history}
          locale={{ emptyText: 'No reminders have been sent to this member yet.' }}
          columns={[
            { title: 'Sent On', dataIndex: 'sentOn', key: 'sentOn', width: 190 },
            { title: 'Documents', dataIndex: 'documents', key: 'documents' },
            { title: 'Remark', dataIndex: 'remark', key: 'remark' },
            { title: 'Sent By', dataIndex: 'sentBy', key: 'sentBy', width: 160 },
          ]}
        />
      </Panel>
    </Collapse>
  );
};

export default DocumentReminderHistory;
